import { useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Award, ChevronDown, Download, FileSpreadsheet, FileText, Loader2, Search } from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/helpers";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Status = ({ e }) => {
  if (e.past) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 whitespace-nowrap" title={e.past.provider ? `Provider: ${e.past.provider}` : undefined}>Completed (previous)</span>;
  if (e.certificate) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Certificate issued</span>;
  if (e.attendance === "absent") return <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Absent</span>;
  if (e.attendance === "present") return <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">Attended</span>;
  const upcoming = e.session?.start && new Date(e.session.start) > new Date();
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{upcoming ? "Upcoming" : "Booked"}</span>;
};

const CertCell = ({ e }) => (e.past || e.certificate?.external) ? <span className="text-xs">{e.certificate.number || "-"}</span> : e.certificate ? (
  <a href={`${BACKEND}/api/access/${e.access_token}/certificate`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs" data-testid={`overview-cert-${e.id}`}>
    <Download className="h-3.5 w-3.5" />{e.certificate.number || "Download"}
  </a>
) : <span className="text-xs text-muted-foreground">-</span>;

const renewalInfo = (e) => {
  const exp = e.certificate?.expires_at;
  if (!e.certificate) return null;
  if (!exp) return { text: "Never expires", tone: "muted" };
  const days = Math.ceil((new Date(exp) - new Date()) / 86400000);
  if (days < 0) return { text: `Expired ${fmtDate(exp)}`, tone: "red", label: "Expired" };
  if (days <= 30) return { text: `Due ${fmtDate(exp)}`, tone: "amber", label: "Due soon" };
  return { text: fmtDate(exp), tone: "muted", label: "Valid" };
};

const toneCls = { red: "text-red-700 font-medium", amber: "text-amber-700 font-medium", muted: "text-muted-foreground" };

const RenewalCell = ({ e }) => {
  const r = renewalInfo(e);
  return r ? <span className={`text-xs whitespace-nowrap ${toneCls[r.tone]}`} data-testid={`overview-renewal-${e.id}`}>{r.text}</span> : <span className="text-xs text-muted-foreground">-</span>;
};

const statusText = (e) => {
  if (e.past) return "Completed (previous)";
  if (e.certificate) return "Certificate issued";
  if (e.attendance === "absent") return "Absent";
  if (e.attendance === "present") return "Attended";
  return e.session?.start && new Date(e.session.start) > new Date() ? "Upcoming" : "Booked";
};

export const dateText = (r) => (r.past ? `Completed ${fmtDate(r.past.completed_on)}` : r.session?.start ? `${fmtDate(r.session.start)} ${fmtTime(r.session.start)}` : "E-learning / any time");

const downloadReport = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Learner", "Email", "Course", "Date", "Time", "Status", "Certificate number", "Certificate issue date", "Renewal due", "Provider"];
  const lines = rows.map((r) => [r.learner, r.learnerEmail, r.course_title,
    r.past ? fmtDate(r.past.completed_on) : r.session?.start ? fmtDate(r.session.start) : "E-learning / any time", r.session?.start ? fmtTime(r.session.start) : "",
    statusText(r), r.certificate?.number || "", r.certificate?.issue_date ? fmtDate(r.certificate.issue_date) : "",
    r.certificate ? (r.certificate.expires_at ? fmtDate(r.certificate.expires_at) : "Never expires") : "", r.past?.provider || ""].map(esc).join(","));
  const blob = new Blob(["\ufeff" + [head.map(esc).join(","), ...lines].join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `team-training-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const LearnerGroup = ({ id, name, email, rows, open, onToggle }) => {
  const certs = rows.filter((r) => r.certificate && !r.past).length;
  const prev = rows.filter((r) => r.past).length;
  const booked = rows.length - prev;
  const dueN = rows.filter((r) => ["Expired", "Due soon"].includes(renewalInfo(r)?.label)).length;
  return (
    <tbody>
      <tr className="border-t border-border cursor-pointer hover:bg-accent/30" onClick={onToggle} data-testid={`overview-learner-${id}`}>
        <td className="p-3" colSpan={6}>
          <div className="flex items-center gap-3">
            <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
            <div className="min-w-0 flex-1"><p className="font-medium">{name}</p><p className="text-xs text-muted-foreground">{email}</p><p className="text-xs text-muted-foreground sm:hidden">{booked} course(s) · {certs} certificate(s){prev ? ` · ${prev} previous` : ""}{dueN ? ` · ${dueN} due` : ""}</p></div>
            <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">{booked} course(s) · {certs} certificate(s){prev ? ` · ${prev} previous` : ""}{dueN ? <span className="text-amber-700 font-medium"> · {dueN} due</span> : null}</span>
          </div>
        </td>
      </tr>
      {open && rows.map((r) => (
        <tr key={r.id} className="border-t border-border bg-muted/20" data-testid={`overview-row-${r.id}`}>
          <td className="p-3 pl-10 text-xs text-muted-foreground">{name}</td>
          <td className="p-3">{r.course_title}</td>
          <td className="p-3 whitespace-nowrap">{dateText(r)}</td>
          <td className="p-3"><Status e={r} /></td>
          <td className="p-3"><CertCell e={r} /></td>
          <td className="p-3"><RenewalCell e={r} /></td>
        </tr>
      ))}
    </tbody>
  );
};

export const TeamOverview = ({ candidates }) => {
  const [q, setQ] = useState("");
  const [openIds, setOpenIds] = useState([]);
  const [secOpen, setSecOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.get("/membership/team-report.pdf", { params: { q }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `team-training-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Could not create the PDF report. Please try again."); }
    setPdfBusy(false);
  };
  const rows = candidates.flatMap((c) => [...(c.enrolments || []), ...(c.history || []).map((h) => ({
    id: h.id, candidate_id: c.id, course_title: h.course_title, past: h,
    certificate: { number: h.certificate_number, issue_date: h.completed_on, expires_at: h.expires_on },
  }))].map((e) => ({ ...e, learner: c.name, learnerEmail: c.email })))
    .filter((r) => !q || `${r.learner} ${r.course_title}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.learner.localeCompare(b.learner) || (a.session?.start || "9999").localeCompare(b.session?.start || "9999"));
  const issued = rows.filter((r) => r.certificate && !r.past).length;
  const prevAll = rows.filter((r) => r.past).length;
  const due = rows.filter((r) => ["Expired", "Due soon"].includes(renewalInfo(r)?.label)).length;
  const groups = Object.values(rows.reduce((acc, r) => {
    (acc[r.candidate_id] = acc[r.candidate_id] || { name: r.learner, email: r.learnerEmail, id: r.candidate_id, rows: [] }).rows.push(r);
    return acc;
  }, {}));
  const allOpen = groups.length > 0 && groups.every((g) => openIds.includes(g.id));
  const toggle = (id) => setOpenIds(openIds.includes(id) ? openIds.filter((x) => x !== id) : [...openIds, id]);
  return (
    <div className="mt-8" data-testid="team-overview">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <button type="button" onClick={() => setSecOpen(!secOpen)} className="font-heading text-lg font-semibold flex items-center gap-2 hover:text-primary" data-testid="overview-toggle"><ChevronDown className={`h-4 w-4 transition-transform ${secOpen || q ? "" : "-rotate-90"}`} /><Award className="h-5 w-5 text-primary" />Team training overview</button>
          <p className="text-sm text-muted-foreground" data-testid="overview-summary">{rows.length - prevAll} booking(s) · {issued} certificate(s) issued{prevAll ? ` · ${prevAll} previous` : ""}{due ? <span className="text-amber-700 font-medium" data-testid="overview-due-count"> · {due} due for renewal</span> : null}
            {secOpen && groups.length > 0 && !q && <button type="button" className="ml-2 text-xs text-primary hover:underline" onClick={() => setOpenIds(allOpen ? [] : groups.map((g) => g.id))} data-testid="overview-expand-all">{allOpen ? "Collapse all" : "Expand all"}</button>}</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
        <Button size="sm" className="h-9 shrink-0" disabled={!rows.length || pdfBusy} onClick={downloadPdf} data-testid="overview-download-pdf-button">{pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}PDF report</Button>
        <Button size="sm" variant="outline" className="h-9 shrink-0" disabled={!rows.length} onClick={() => downloadReport(rows)} data-testid="overview-download-button"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Spreadsheet</Button>
        <div className="relative w-full sm:w-64 basis-full sm:basis-auto">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="pl-8 h-9" data-testid="overview-search-input" />
        </div>
        </div>
      </div>
      {(secOpen || q) && <div className="mt-3 overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-3">Learner</th><th className="p-3">Course</th><th className="p-3">Date</th><th className="p-3">Status</th><th className="p-3">Certificate</th><th className="p-3">Renewal due</th></tr>
          </thead>
          {rows.length === 0 && <tbody><tr><td colSpan={6} className="p-4 text-center text-muted-foreground" data-testid="overview-empty">{q ? "No bookings match your search." : "No bookings yet."}</td></tr></tbody>}
          {groups.map((g) => <LearnerGroup key={g.id} {...g} open={!!q || openIds.includes(g.id)} onToggle={() => toggle(g.id)} />)}
        </table>
      </div>}
    </div>
  );
};
